import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { productionTestAPI, datasetAPI } from '../services/api';

const MAX_CASES = 1000;

// ─── Converters ─────────────────────────────────────────────────────────────

/** Merge globalParams (overridden by per-case params) and produce API shape */
const toApiFormat = (cases, globalParams = []) => {
  const globalObj = Object.fromEntries(
    globalParams.filter(p => p.key.trim()).map(p => [p.key.trim(), p.value])
  );
  return cases.map(tc => ({
    id: tc.id,
    query_params: {
      ...globalObj,
      ...Object.fromEntries(tc.params.filter(p => p.key.trim()).map(p => [p.key.trim(), p.value]))
    }
  }));
};

const fromApiFormat = (cases) =>
  (cases || []).map(tc => ({
    id: tc.id || '',
    params: Object.entries(tc.query_params || {}).map(([key, value]) => ({ key, value: String(value) }))
  }));

/** Accept {id, query_params} or flat objects */
const parseAnyJson = (jsonStr) => {
  const data = JSON.parse(jsonStr);
  if (!Array.isArray(data)) throw new Error('Expected a JSON array');
  return data.map((item, i) => {
    let id, queryParams;
    if ('query_params' in item) {
      id = item.id || `test_${i + 1}`;
      queryParams = item.query_params || {};
    } else {
      const { id: itemId, ...rest } = item;
      id = itemId || `test_${i + 1}`;
      queryParams = rest;
    }
    return { id, params: Object.entries(queryParams).map(([k, v]) => ({ key: k, value: String(v) })) };
  });
};

const parseCsv = (text) => {
  const lines = text.trim().split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row');
  const parseRow = (l) => l.split(',').map(c => c.trim().replace(/^"(.*)"$/, '$1'));
  const headers = parseRow(lines[0]);
  const idIdx = headers.findIndex(h => h.toLowerCase() === 'id');
  return lines.slice(1).map((line, i) => {
    const vals = parseRow(line);
    const id = idIdx >= 0 ? (vals[idIdx] || `test_${i + 1}`) : `test_${i + 1}`;
    const params = headers
      .filter((_, j) => j !== idIdx)
      .map((key, j) => { const vi = j < idIdx || idIdx < 0 ? j : j + 1; return { key, value: vals[vi] || '' }; });
    return { id, params };
  });
};

const nextId = (cases) => `test_${cases.length + 1}`;

// ─── Small UI helpers ────────────────────────────────────────────────────────

function TabButton({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '0.5rem 1.25rem', border: 'none',
      borderBottom: active ? '3px solid #3498db' : '3px solid transparent',
      background: 'none', fontWeight: active ? 700 : 400,
      color: active ? '#3498db' : '#555', cursor: 'pointer', fontSize: '0.95rem'
    }}>
      {label}
    </button>
  );
}

function SectionHeader({ title, count, open, onToggle, badge }) {
  return (
    <div
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        cursor: 'pointer', padding: '0.6rem 0.75rem',
        background: '#f0f4f8', borderRadius: open ? '6px 6px 0 0' : '6px',
        border: '1px solid #dde3ea', userSelect: 'none', marginBottom: open ? 0 : '0.75rem'
      }}
    >
      <span style={{ fontSize: '0.9rem', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
      <strong style={{ fontSize: '0.95rem' }}>{title}</strong>
      {count != null && (
        <span style={{ marginLeft: '0.25rem', fontSize: '0.8rem', color: '#3498db', fontWeight: 600 }}>
          ({count})
        </span>
      )}
      {badge && <span style={{ marginLeft: 'auto', fontSize: '0.78rem', background: '#3498db', color: '#fff', borderRadius: '10px', padding: '0.15rem 0.6rem' }}>{badge}</span>}
    </div>
  );
}

// ─── Global Params ───────────────────────────────────────────────────────────

function GlobalParamsSection({ globalParams, setGlobalParams }) {
  const [open, setOpen] = useState(true);

  const addParam = () => setGlobalParams(p => [...p, { key: '', value: '' }]);
  const removeParam = (i) => setGlobalParams(p => p.filter((_, idx) => idx !== i));
  const updateParam = (i, field, val) =>
    setGlobalParams(p => p.map((param, idx) => idx === i ? { ...param, [field]: val } : param));

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <SectionHeader
        title="Global Parameters"
        count={globalParams.filter(p => p.key.trim()).length || undefined}
        open={open}
        onToggle={() => setOpen(o => !o)}
        badge="added to all test cases"
      />
      {open && (
        <div style={{ border: '1px solid #dde3ea', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '0.75rem 1rem' }}>
          <p style={{ fontSize: '0.82rem', color: '#666', marginBottom: '0.6rem' }}>
            These key-value pairs are merged into every test case. Per-case params override global ones if keys conflict.
          </p>
          {globalParams.length === 0
            ? <p style={{ color: '#aaa', fontSize: '0.85rem' }}>No global params yet.</p>
            : globalParams.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem' }}>
                <input
                  type="text" value={p.key} placeholder="key"
                  onChange={e => updateParam(i, 'key', e.target.value)}
                  style={{ width: '160px', padding: '0.3rem 0.5rem', fontSize: '0.85rem', fontFamily: 'monospace', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <span style={{ color: '#999' }}>=</span>
                <input
                  type="text" value={p.value} placeholder="value"
                  onChange={e => updateParam(i, 'value', e.target.value)}
                  style={{ flex: 1, padding: '0.3rem 0.5rem', fontSize: '0.85rem', fontFamily: 'monospace', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <button type="button" onClick={() => removeParam(i)}
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', cursor: 'pointer', color: '#c33' }}>
                  ×
                </button>
              </div>
            ))
          }
          <button type="button" onClick={addParam} className="btn btn-secondary" style={{ marginTop: '0.25rem', fontSize: '0.85rem', padding: '0.4rem 0.9rem' }}>
            + Add Global Param
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Dataset Generator ───────────────────────────────────────────────────────

function DatasetGenerator({ cases, setCases }) {
  const [open, setOpen] = useState(false);
  const [datasets, setDatasets] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [mappings, setMappings] = useState([]); // [{col, paramName, include}]
  const [idSource, setIdSource] = useState('auto');   // 'auto' | 'column'
  const [idColumn, setIdColumn] = useState('');
  const [idPrefix, setIdPrefix] = useState('test_');
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState(null);
  const [appendCount, setAppendCount] = useState('');

  useEffect(() => {
    datasetAPI.getAll().then(r => setDatasets(r.data)).catch(() => {});
  }, []);

  const selectedDataset = datasets.find(d => d._id === selectedId);

  const handleDatasetChange = (id) => {
    setSelectedId(id);
    setGenStatus(null);
    setAppendCount('');
    const ds = datasets.find(d => d._id === id);
    if (!ds) { setMappings([]); return; }
    setMappings((ds.columns || []).map(col => ({ col, paramName: col, include: true })));
    setIdColumn('');
  };

  const toggleInclude = (i) =>
    setMappings(m => m.map((row, idx) => idx === i ? { ...row, include: !row.include } : row));
  const updateParamName = (i, val) =>
    setMappings(m => m.map((row, idx) => idx === i ? { ...row, paramName: val } : row));

  const generate = async (mode, explicitCount) => {
    if (!selectedId) return;
    setGenerating(true);
    setGenStatus(null);
    try {
      const res = await datasetAPI.getById(selectedId);
      const rows = res.data.data || [];
      const activeMappings = mappings.filter(m => m.include && m.paramName.trim());

      const cap = mode === 'replace' ? MAX_CASES : MAX_CASES - cases.length;
      const limit = explicitCount != null ? Math.min(explicitCount, cap) : cap;
      const rowsToUse = rows.slice(0, limit);
      const skipped = rows.length - rowsToUse.length;

      const generated = rowsToUse.map((row, i) => {
        const id = idSource === 'column' && idColumn
          ? (String(row[idColumn] || '').replace(/\s+/g, '_') || `${idPrefix}${i + 1}`)
          : `${idPrefix}${i + 1}`;
        const params = activeMappings.map(m => ({ key: m.paramName.trim(), value: String(row[m.col] ?? '') }));
        return { id, params };
      });

      if (mode === 'replace') {
        setCases(generated);
      } else {
        setCases(prev => {
          const offset = prev.length;
          return [...prev, ...generated.map((tc, i) => ({
            ...tc,
            id: idSource === 'column' && idColumn ? tc.id : `${idPrefix}${offset + i + 1}`
          }))];
        });
      }
      const skippedNote = skipped > 0 ? ` (${skipped.toLocaleString()} skipped — limit ${MAX_CASES})` : '';
      setGenStatus({ ok: true, msg: `${mode === 'replace' ? 'Replaced with' : 'Appended'} ${generated.length.toLocaleString()} cases from "${selectedDataset?.name}"${skippedNote}` });
    } catch (err) {
      setGenStatus({ ok: false, msg: err.message });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <SectionHeader title="Generate from Dataset" open={open} onToggle={() => setOpen(o => !o)} />
      {open && (
        <div style={{ border: '1px solid #dde3ea', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '1rem' }}>
          {datasets.length === 0 ? (
            <div className="alert alert-info" style={{ margin: 0 }}>
              No datasets found. <Link to="/datasets">Upload a dataset</Link> first.
            </div>
          ) : (
            <>
              {/* Dataset picker */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 280px' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Dataset</label>
                  <select value={selectedId} onChange={e => handleDatasetChange(e.target.value)} style={{ width: '100%' }}>
                    <option value="">— Select a dataset —</option>
                    {datasets.map(d => (
                      <option key={d._id} value={d._id}>
                        {d.name} ({(d.rowCount || 0).toLocaleString()} rows, {(d.columns || []).length} cols)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Column mappings */}
              {selectedDataset && mappings.length > 0 && (
                <>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.4rem' }}>
                      Column → Parameter Name mapping
                    </label>
                    <div style={{ border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', fontSize: '0.83rem', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f5f5f5' }}>
                            <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', width: '40px', borderBottom: '1px solid #e0e0e0' }}>Use</th>
                            <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Dataset Column</th>
                            <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Parameter Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mappings.map((m, i) => (
                            <tr key={i} style={{ background: m.include ? 'white' : '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '0.35rem 0.6rem', textAlign: 'center' }}>
                                <input type="checkbox" checked={m.include} onChange={() => toggleInclude(i)} />
                              </td>
                              <td style={{ padding: '0.35rem 0.6rem' }}>
                                <code style={{ fontSize: '0.82rem', color: '#555' }}>{m.col}</code>
                              </td>
                              <td style={{ padding: '0.35rem 0.6rem' }}>
                                {m.include
                                  ? (
                                    <input
                                      type="text"
                                      value={m.paramName}
                                      onChange={e => updateParamName(i, e.target.value)}
                                      style={{ width: '200px', padding: '0.25rem 0.4rem', fontFamily: 'monospace', fontSize: '0.82rem', border: '1px solid #ccc', borderRadius: '3px' }}
                                    />
                                  )
                                  : <span style={{ color: '#aaa', fontSize: '0.8rem' }}>excluded</span>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* ID config */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                    <div>
                      <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Test Case ID source</label>
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem' }}>
                          <input type="radio" value="auto" checked={idSource === 'auto'} onChange={() => setIdSource('auto')} /> Auto-generate
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem' }}>
                          <input type="radio" value="column" checked={idSource === 'column'} onChange={() => setIdSource('column')} /> From column
                        </label>
                      </div>
                    </div>

                    {idSource === 'auto' && (
                      <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>ID Prefix</label>
                        <input
                          type="text" value={idPrefix} onChange={e => setIdPrefix(e.target.value)}
                          style={{ width: '120px', padding: '0.3rem 0.5rem', fontSize: '0.85rem', fontFamily: 'monospace', border: '1px solid #ccc', borderRadius: '4px' }}
                          placeholder="test_"
                        />
                        <span style={{ marginLeft: '0.4rem', fontSize: '0.8rem', color: '#888' }}>→ {idPrefix}1, {idPrefix}2…</span>
                      </div>
                    )}

                    {idSource === 'column' && (
                      <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>ID Column</label>
                        <select value={idColumn} onChange={e => setIdColumn(e.target.value)} style={{ padding: '0.3rem 0.5rem', fontSize: '0.85rem' }}>
                          <option value="">— pick column —</option>
                          {(selectedDataset?.columns || []).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  {(() => {
                    const remaining = MAX_CASES - cases.length;
                    const rowCount = selectedDataset?.rowCount || 0;
                    const allCount = Math.min(rowCount, remaining);
                    const replaceCount = Math.min(rowCount, MAX_CASES);
                    const nVal = Math.max(1, Math.min(parseInt(appendCount) || 0, rowCount, remaining));
                    return (
                      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        {/* Append all (capped) */}
                        <button
                          type="button"
                          onClick={() => generate('append')}
                          disabled={generating || remaining <= 0}
                          className="btn btn-primary"
                          style={{ fontSize: '0.88rem' }}
                          title={rowCount > remaining ? `Dataset has ${rowCount.toLocaleString()} rows; only ${remaining} slots left` : undefined}
                        >
                          {generating ? 'Generating…' : `Append ${allCount.toLocaleString()}${rowCount > remaining ? ` of ${rowCount.toLocaleString()}` : ''}`}
                        </button>

                        {/* Append specific N */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <span style={{ fontSize: '0.85rem', color: '#555' }}>or first</span>
                          <input
                            type="number"
                            value={appendCount}
                            onChange={e => setAppendCount(e.target.value)}
                            placeholder="N"
                            min={1}
                            max={Math.min(rowCount, remaining)}
                            disabled={remaining <= 0}
                            style={{ width: '80px', padding: '0.35rem 0.4rem', fontSize: '0.85rem', border: '1px solid #ccc', borderRadius: '4px' }}
                          />
                          <button
                            type="button"
                            onClick={() => generate('append', nVal)}
                            disabled={generating || remaining <= 0 || !appendCount || parseInt(appendCount) < 1}
                            className="btn btn-secondary"
                            style={{ fontSize: '0.85rem' }}
                          >
                            Append
                          </button>
                        </div>

                        {/* Replace */}
                        <button
                          type="button"
                          onClick={() => generate('replace')}
                          disabled={generating}
                          className="btn btn-secondary"
                          style={{ fontSize: '0.88rem' }}
                        >
                          {generating ? '…' : `Replace (${replaceCount.toLocaleString()}${rowCount > MAX_CASES ? ` of ${rowCount.toLocaleString()}` : ''})`}
                        </button>

                        {remaining <= 0 && !generating && (
                          <span style={{ fontSize: '0.82rem', color: '#e74c3c' }}>No slots left — use Replace</span>
                        )}
                      </div>
                    );
                  })()}

                  {genStatus && (
                    <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.85rem', background: genStatus.ok ? '#d4edda' : '#f8d7da', color: genStatus.ok ? '#155724' : '#721c24' }}>
                      {genStatus.msg}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Builder Tab ─────────────────────────────────────────────────────────────

function BuilderTab({ cases, setCases, globalParams, setGlobalParams }) {
  const addCase = () =>
    setCases(prev => [...prev, { id: nextId(prev), params: [{ key: '', value: '' }] }]);
  const removeCase = (i) => setCases(prev => prev.filter((_, idx) => idx !== i));
  const updateId = (i, val) => setCases(prev => prev.map((tc, idx) => idx === i ? { ...tc, id: val } : tc));
  const addParam = (i) =>
    setCases(prev => prev.map((tc, idx) => idx === i ? { ...tc, params: [...tc.params, { key: '', value: '' }] } : tc));
  const removeParam = (ci, pi) =>
    setCases(prev => prev.map((tc, idx) => idx === ci ? { ...tc, params: tc.params.filter((_, pidx) => pidx !== pi) } : tc));
  const updateParam = (ci, pi, field, val) =>
    setCases(prev => prev.map((tc, idx) => idx === ci
      ? { ...tc, params: tc.params.map((p, pidx) => pidx === pi ? { ...p, [field]: val } : p) }
      : tc
    ));

  return (
    <div>
      <GlobalParamsSection globalParams={globalParams} setGlobalParams={setGlobalParams} />
      <DatasetGenerator cases={cases} setCases={setCases} />

      {/* Test cases list */}
      <div style={{ marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: '0.95rem' }}>Test Cases</strong>
          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: cases.length >= MAX_CASES ? '#e74c3c' : cases.length >= MAX_CASES * 0.9 ? '#f39c12' : '#3498db' }}>
            {cases.length} / {MAX_CASES}
          </span>
          {cases.length >= MAX_CASES && (
            <span style={{ fontSize: '0.78rem', background: '#f8d7da', color: '#721c24', padding: '0.15rem 0.5rem', borderRadius: '10px' }}>
              Limit reached
            </span>
          )}
          {globalParams.filter(p => p.key.trim()).length > 0 && (
            <span style={{ fontSize: '0.78rem', color: '#888', marginLeft: '0.25rem' }}>
              + {globalParams.filter(p => p.key.trim()).length} global param{globalParams.filter(p => p.key.trim()).length !== 1 ? 's' : ''} on each
            </span>
          )}
        </div>

        {cases.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1.5rem', color: '#888', background: '#fafafa', borderRadius: '6px', border: '1px dashed #ddd', fontSize: '0.9rem' }}>
            No test cases yet — add manually, generate from dataset, or import via JSON/File tab.
          </div>
        ) : (
          <div style={{ maxHeight: '480px', overflowY: 'auto', paddingRight: '2px' }}>
            {cases.map((tc, ci) => (
              <div key={ci} style={{ border: '1px solid #e0e0e0', borderRadius: '6px', padding: '0.6rem 0.75rem', marginBottom: '0.4rem', background: '#fafafa' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.72rem', color: '#bbb', minWidth: '20px' }}>#{ci + 1}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <span style={{ fontSize: '0.78rem', color: '#555', fontWeight: 500 }}>ID:</span>
                    <input
                      type="text" value={tc.id} onChange={e => updateId(ci, e.target.value)}
                      style={{ width: '180px', padding: '0.25rem 0.5rem', fontFamily: 'monospace', fontSize: '0.8rem', border: '1px solid #ccc', borderRadius: '4px' }}
                      placeholder="test_id"
                    />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', flex: 1 }}>
                    {tc.params.map((p, pi) => (
                      <div key={pi} style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                        <input
                          type="text" value={p.key} onChange={e => updateParam(ci, pi, 'key', e.target.value)}
                          placeholder="key"
                          style={{ width: '105px', padding: '0.25rem 0.4rem', fontSize: '0.78rem', fontFamily: 'monospace', border: '1px solid #ccc', borderRadius: '3px' }}
                        />
                        <span style={{ color: '#bbb', fontSize: '0.75rem' }}>=</span>
                        <input
                          type="text" value={p.value} onChange={e => updateParam(ci, pi, 'value', e.target.value)}
                          placeholder="value"
                          style={{ width: '125px', padding: '0.25rem 0.4rem', fontSize: '0.78rem', fontFamily: 'monospace', border: '1px solid #ccc', borderRadius: '3px' }}
                        />
                        <button type="button" onClick={() => removeParam(ci, pi)}
                          style={{ padding: '0.15rem 0.4rem', fontSize: '0.72rem', background: '#fee', border: '1px solid #fcc', borderRadius: '3px', cursor: 'pointer', color: '#c33', lineHeight: 1 }}>
                          ×
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={() => addParam(ci)}
                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.72rem', background: '#e8f4fd', border: '1px solid #aad', borderRadius: '3px', cursor: 'pointer', color: '#3498db', fontWeight: 600 }}>
                      + param
                    </button>
                  </div>
                  <button type="button" onClick={() => removeCase(ci)}
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.78rem', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', cursor: 'pointer', color: '#c33', flexShrink: 0 }}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={addCase} disabled={cases.length >= MAX_CASES} className="btn btn-secondary" style={{ marginTop: '0.5rem', fontSize: '0.88rem' }}>
          + Add Test Case
        </button>
        {cases.length >= MAX_CASES && (
          <span style={{ marginLeft: '0.75rem', fontSize: '0.82rem', color: '#e74c3c' }}>
            Remove some cases to add more (max {MAX_CASES})
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Paste JSON Tab ──────────────────────────────────────────────────────────

function PasteJsonTab({ cases, setCases, globalParams }) {
  const [text, setText] = useState('');
  const [parseError, setParseError] = useState(null);
  const [parseSuccess, setParseSuccess] = useState(null);

  useEffect(() => {
    if (cases.length > 0) setText(JSON.stringify(toApiFormat(cases, globalParams), null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleParse = () => {
    try {
      if (!text.trim()) throw new Error('Paste some JSON first');
      const parsed = parseAnyJson(text);
      setCases(parsed);
      setParseError(null);
      setParseSuccess(`Loaded ${parsed.length} test case${parsed.length !== 1 ? 's' : ''}`);
    } catch (e) { setParseError(e.message); setParseSuccess(null); }
  };

  return (
    <div>
      <p style={{ fontSize: '0.84rem', color: '#666', marginBottom: '0.6rem' }}>
        Paste any JSON array — standard <code>{`[{"id":"…","query_params":{…}}]`}</code>, flat objects, or the n8n <code>test_cases</code> array.
        {globalParams.filter(p => p.key.trim()).length > 0 && (
          <strong style={{ color: '#3498db' }}> Note: global params will be merged on save (not shown here).</strong>
        )}
      </p>
      <textarea
        value={text}
        onChange={e => { setText(e.target.value); setParseError(null); setParseSuccess(null); }}
        rows={16}
        style={{ fontFamily: 'monospace', fontSize: '0.82rem', border: parseError ? '2px solid #e74c3c' : '1px solid #ddd', borderRadius: '4px', padding: '0.75rem', width: '100%', resize: 'vertical' }}
        placeholder={`[\n  { "id": "test_1", "query_params": { "asin": "B0B3MWR56W", "domain": "de" } },\n  { "asin": "B096433ZRV", "domain": "es" }\n]`}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
        <button type="button" onClick={handleParse} className="btn btn-primary">Parse &amp; Load</button>
        {parseError && <span style={{ color: '#e74c3c', fontSize: '0.85rem' }}>Error: {parseError}</span>}
        {parseSuccess && <span style={{ color: '#27ae60', fontSize: '0.85rem' }}>{parseSuccess}</span>}
      </div>
    </div>
  );
}

// ─── File Tab ─────────────────────────────────────────────────────────────────

function FileTab({ cases, setCases }) {
  const fileRef = useRef(null);
  const [parsed, setParsed] = useState(null);  // null = no file loaded
  const [fileName, setFileName] = useState('');
  const [addCount, setAddCount] = useState('');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const remaining = MAX_CASES - cases.length;

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError(null); setStatus(null); setParsed(null);
    const ext = file.name.split('.').pop().toLowerCase();
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const result = ext === 'json'
          ? parseAnyJson(text)
          : ext === 'csv'
            ? parseCsv(text)
            : (() => { throw new Error('Unsupported file type — use .json or .csv'); })();
        setParsed(result);
        setFileName(file.name);
        setAddCount(String(Math.min(result.length, remaining)));
      } catch (err) { setError(err.message); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const doAdd = (n) => {
    const toAdd = parsed.slice(0, n);
    setCases(prev => [...prev, ...toAdd]);
    setStatus(`Added ${toAdd.length} case${toAdd.length !== 1 ? 's' : ''} from "${fileName}". ${n < parsed.length ? `(${parsed.length - n} skipped)` : ''}`);
    setParsed(null);
  };

  const handleAddAll = () => doAdd(Math.min(parsed.length, remaining));
  const handleAddN = () => {
    const n = Math.max(1, Math.min(parseInt(addCount) || 0, parsed.length, remaining));
    doAdd(n);
  };

  return (
    <div>
      <p style={{ fontSize: '0.84rem', color: '#666', marginBottom: '1rem' }}>
        Upload <strong>.json</strong> (same formats as Paste JSON) or <strong>.csv</strong> (header row required; <code>id</code> column = test case ID, all other columns = query params).
      </p>

      {/* Step 1 — pick file */}
      {!parsed && (
        <>
          <div
            onClick={() => remaining > 0 && fileRef.current?.click()}
            style={{
              border: '2px dashed ' + (remaining <= 0 ? '#e0e0e0' : '#bbb'),
              borderRadius: '8px', padding: '2rem', textAlign: 'center',
              background: remaining <= 0 ? '#f5f5f5' : '#fafafa',
              cursor: remaining > 0 ? 'pointer' : 'not-allowed'
            }}
          >
            <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }}>📂</div>
            {remaining <= 0 ? (
              <div style={{ color: '#e74c3c', fontWeight: 600 }}>Limit reached ({MAX_CASES} / {MAX_CASES}) — remove cases first</div>
            ) : (
              <>
                <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>Click to select file</div>
                <div style={{ fontSize: '0.83rem', color: '#888' }}>Accepts .json and .csv · {remaining} slot{remaining !== 1 ? 's' : ''} remaining</div>
              </>
            )}
            <input ref={fileRef} type="file" accept=".json,.csv" onChange={handleFile} style={{ display: 'none' }} disabled={remaining <= 0} />
          </div>
          {status && <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.9rem', background: '#d4edda', borderRadius: '4px', color: '#155724', fontSize: '0.88rem' }}>{status}</div>}
          {error && <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.9rem', background: '#f8d7da', borderRadius: '4px', color: '#721c24', fontSize: '0.88rem' }}>Error: {error}</div>}
        </>
      )}

      {/* Step 2 — choose how many to add */}
      {parsed && (
        <div style={{ border: '1px solid #c5d9ef', borderRadius: '8px', padding: '1.25rem', background: '#f0f6fb' }}>
          <div style={{ marginBottom: '0.9rem' }}>
            <strong style={{ fontSize: '0.95rem' }}>📄 {fileName}</strong>
            <div style={{ marginTop: '0.3rem', fontSize: '0.86rem', color: '#444' }}>
              Found <strong>{parsed.length.toLocaleString()}</strong> case{parsed.length !== 1 ? 's' : ''} in file.
              {' '}Current total: <strong>{cases.length}</strong> / {MAX_CASES}.
              {' '}Remaining slots: <strong style={{ color: remaining < parsed.length ? '#f39c12' : '#27ae60' }}>{remaining}</strong>.
              {remaining === 0 && <span style={{ color: '#e74c3c', fontWeight: 600 }}> No slots left!</span>}
            </div>
          </div>

          {remaining > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleAddAll}
                className="btn btn-primary"
                style={{ fontSize: '0.9rem' }}
              >
                Add All ({Math.min(parsed.length, remaining).toLocaleString()}{parsed.length > remaining ? ` of ${parsed.length.toLocaleString()}` : ''})
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontSize: '0.85rem', color: '#555' }}>Add first</span>
                <input
                  type="number"
                  value={addCount}
                  onChange={e => setAddCount(e.target.value)}
                  min={1}
                  max={Math.min(parsed.length, remaining)}
                  style={{ width: '90px', padding: '0.35rem 0.5rem', fontSize: '0.88rem', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <span style={{ fontSize: '0.85rem', color: '#555' }}>cases</span>
                <button
                  type="button"
                  onClick={handleAddN}
                  disabled={!addCount || parseInt(addCount) < 1}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.88rem' }}
                >
                  Add
                </button>
              </div>

              <button type="button" onClick={() => { setParsed(null); setError(null); }} className="btn btn-secondary" style={{ fontSize: '0.85rem' }}>
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <span style={{ color: '#e74c3c', fontSize: '0.88rem' }}>No remaining slots. Remove existing cases first.</span>
              <button type="button" onClick={() => setParsed(null)} className="btn btn-secondary" style={{ fontSize: '0.85rem' }}>Dismiss</button>
            </div>
          )}
        </div>
      )}

      {!parsed && (
        <pre style={{ marginTop: '0.75rem', background: '#f5f5f5', padding: '0.6rem 0.8rem', borderRadius: '4px', fontSize: '0.78rem', color: '#555' }}>
{`CSV example:
id,asin,domain
amazon_de_B0B3,B0B3MWR56W,de
amazon_es_B096,B096433ZRV,es`}
        </pre>
      )}
    </div>
  );
}

// ─── EMPTY FORM ───────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '', description: '', folder: '', endpoint: '', method: 'GET',
  auth: { type: 'none', token: '', key_name: 'x-api-key', key_value: '', username: '', password: '' },
  concurrency: 10, produce_outputs: false, success_codes: [200], timeout: 30
};

// ─── MAIN ─────────────────────────────────────────────────────────────────────

function CreateProductionTest() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [cases, setCases] = useState([]);
  const [globalParams, setGlobalParams] = useState([]);
  const [activeTab, setActiveTab] = useState('builder');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [error, setError] = useState(null);
  const [folders, setFolders] = useState([]);
  const [newFolderMode, setNewFolderMode] = useState(false);

  useEffect(() => {
    productionTestAPI.getFolders().then(r => setFolders(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      productionTestAPI.getById(id)
        .then(res => {
          const { test_cases, ...rest } = res.data;
          setForm({ ...EMPTY_FORM, ...rest, auth: { ...EMPTY_FORM.auth, ...(rest.auth || {}) } });
          setCases(fromApiFormat(test_cases));
        })
        .catch(err => setError(err.message))
        .finally(() => setFetchLoading(false));
    }
  }, [id, isEdit]);

  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));
  const setAuth = (name, value) => setForm(prev => ({ ...prev, auth: { ...prev.auth, [name]: value } }));
  const handleSuccessCodes = (val) => {
    const codes = val.split(',').map(c => parseInt(c.trim())).filter(c => !isNaN(c));
    setField('success_codes', codes);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.folder && !/^[a-zA-Z0-9_]+$/.test(form.folder)) {
      setError('Folder name can only contain letters, digits, and underscores (no spaces).');
      return;
    }
    if (cases.length === 0) { setError('Add at least one test case'); return; }
    if (cases.length > MAX_CASES) { setError(`Too many test cases — maximum is ${MAX_CASES} (currently ${cases.length})`); return; }
    const test_cases = toApiFormat(cases, globalParams);
    if (test_cases.some(tc => !tc.id)) { setError('All test cases must have an ID'); return; }

    setLoading(true); setError(null);
    try {
      const payload = { ...form, test_cases };
      if (isEdit) {
        await productionTestAPI.update(id, payload);
        navigate(`/production-tests/test/${id}`);
      } else {
        const res = await productionTestAPI.create(payload);
        navigate(`/production-tests/test/${res.data._id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div>
      <div className="card">
        <h2>{isEdit ? 'Edit Production Test' : 'Create Production Test'}</h2>
        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Basic info */}
          <div className="grid grid-2">
            <div className="form-group">
              <label>Name *</label>
              <input type="text" value={form.name} onChange={e => setField('name', e.target.value)} required placeholder="e.g. Amazon Product Search" />
            </div>
            <div className="form-group">
              <label>Description</label>
              <input type="text" value={form.description} onChange={e => setField('description', e.target.value)} placeholder="Optional" />
            </div>
          </div>

          {/* Folder */}
          <div className="form-group" style={{ maxWidth: '420px' }}>
            <label>📁 Folder <span style={{ fontWeight: 400, color: '#888', fontSize: '0.85rem' }}>(optional · letters, digits, underscores only)</span></label>
            {!newFolderMode ? (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <select
                  value={form.folder}
                  onChange={e => setField('folder', e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="">— No folder (Uncategorized) —</option>
                  {folders.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <button type="button" className="btn btn-secondary" style={{ fontSize: '0.82rem', padding: '0.4rem 0.7rem', whiteSpace: 'nowrap' }}
                  onClick={() => { setNewFolderMode(true); setField('folder', ''); }}>
                  + New
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="text" value={form.folder}
                  onChange={e => setField('folder', e.target.value)}
                  placeholder="Folder name, e.g. Amazon"
                  autoFocus style={{ flex: 1 }}
                />
                <button type="button" className="btn btn-secondary" style={{ fontSize: '0.82rem', padding: '0.4rem 0.7rem' }}
                  onClick={() => { setNewFolderMode(false); setField('folder', ''); }}>
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Endpoint */}
          <div className="grid grid-2">
            <div className="form-group">
              <label>API Endpoint URL *</label>
              <input type="url" value={form.endpoint} onChange={e => setField('endpoint', e.target.value)} required placeholder="https://api-marketplace.webparsers.com/amazon/product" />
              <small>Passed to Lambda as the endpoint under test</small>
            </div>
            <div className="form-group">
              <label>HTTP Method</label>
              <select value={form.method} onChange={e => setField('method', e.target.value)}>
                <option>GET</option><option>POST</option><option>PUT</option><option>DELETE</option><option>PATCH</option>
              </select>
            </div>
          </div>

          {/* Auth */}
          <div className="form-group">
            <label>Authentication</label>
            <select value={form.auth.type} onChange={e => setAuth('type', e.target.value)}>
              <option value="none">None</option>
              <option value="api_key">API Key (header)</option>
              <option value="bearer">Bearer Token</option>
              <option value="basic">Basic Auth</option>
            </select>
          </div>
          {form.auth.type === 'api_key' && (
            <div className="grid grid-2">
              <div className="form-group">
                <label>Key Header Name</label>
                <input type="text" value={form.auth.key_name} onChange={e => setAuth('key_name', e.target.value)} placeholder="x-api-key" />
              </div>
              <div className="form-group">
                <label>Key Value</label>
                <input type="text" value={form.auth.key_value} onChange={e => setAuth('key_value', e.target.value)} />
              </div>
            </div>
          )}
          {form.auth.type === 'bearer' && (
            <div className="form-group">
              <label>Token</label>
              <input type="text" value={form.auth.token} onChange={e => setAuth('token', e.target.value)} />
            </div>
          )}
          {form.auth.type === 'basic' && (
            <div className="grid grid-2">
              <div className="form-group">
                <label>Username</label>
                <input type="text" value={form.auth.username} onChange={e => setAuth('username', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" value={form.auth.password} onChange={e => setAuth('password', e.target.value)} />
              </div>
            </div>
          )}

          {/* Execution params */}
          <div className="grid grid-3">
            <div className="form-group">
              <label>Concurrency</label>
              <input type="number" value={form.concurrency} onChange={e => setField('concurrency', parseInt(e.target.value) || 10)} min="1" max="100" />
              <small>Parallel requests</small>
            </div>
            <div className="form-group">
              <label>Timeout (seconds)</label>
              <input type="number" value={form.timeout} onChange={e => setField('timeout', parseInt(e.target.value) || 30)} min="1" max="300" />
            </div>
            <div className="form-group">
              <label>Success Codes (comma-separated)</label>
              <input type="text" value={form.success_codes.join(', ')} onChange={e => handleSuccessCodes(e.target.value)} placeholder="200, 404" />
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" checked={form.produce_outputs} onChange={e => setField('produce_outputs', e.target.checked)} style={{ width: 'auto' }} />
              Include detailed outputs (save full response bodies)
            </label>
          </div>

          {/* Test Cases section */}
          <div style={{ marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.25rem' }}>
              <label style={{ fontWeight: 600, fontSize: '1rem' }}>Test Cases</label>
              {cases.length > 0 && <span style={{ fontSize: '0.82rem', color: '#3498db', fontWeight: 600 }}>{cases.length} loaded</span>}
            </div>
            <div style={{ display: 'flex', borderBottom: '1px solid #ddd', marginBottom: '1rem' }}>
              <TabButton label="Builder" active={activeTab === 'builder'} onClick={() => setActiveTab('builder')} />
              <TabButton label="Paste JSON" active={activeTab === 'json'} onClick={() => setActiveTab('json')} />
              <TabButton label="Upload File" active={activeTab === 'file'} onClick={() => setActiveTab('file')} />
            </div>

            {activeTab === 'builder' && <BuilderTab cases={cases} setCases={setCases} globalParams={globalParams} setGlobalParams={setGlobalParams} />}
            {activeTab === 'json' && <PasteJsonTab cases={cases} setCases={setCases} globalParams={globalParams} />}
            {activeTab === 'file' && <FileTab cases={cases} setCases={setCases} />}
          </div>

          <div className="flex mt-3" style={{ paddingTop: '1rem', borderTop: '1px solid #eee' }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Test')}
            </button>
            <Link to={isEdit ? `/production-tests/test/${id}` : '/production-tests'} className="btn btn-secondary">Cancel</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateProductionTest;
